import React, { PureComponent } from 'react';
import { DatePicker, Form, FormInstance, Input, Select } from 'antd';
import { MonitoredEvent } from 'app/model/SOL49';
import Utils from 'app/common/util/Utils';
import moment from 'moment';
import TimeUtils from 'app/common/util/TimeUtils';
import './MonitoredEventForm.less';
import { SwapRightOutlined } from '@ant-design/icons';

export interface MonitoredEventFormProps<T> {
    event?: T;
    onCreate: (event: T) => void;
    onUpdate: (event: T) => void;
    onDelete?: () => void;
    onWidthChange?: (widthEm: string) => void;
}

export interface MonitoredEventFormState {
    createDisabled: boolean;
    updateDisabled: boolean;
}

export type MonitoredEventFormValues<T extends MonitoredEvent> = Omit<T, 'validity'> & { validity: moment.Moment[] };

export abstract class MonitoredEventForm<
    T extends MonitoredEvent,
    V extends MonitoredEventFormValues<T>,
    P extends MonitoredEventFormProps<T> = MonitoredEventFormProps<T>,
    S extends MonitoredEventFormState = MonitoredEventFormState,
> extends PureComponent<P, S> {
    protected static FORM_WIDTH_EM = '40em';

    protected formRef = React.createRef<FormInstance>();

    protected selectOptionsSort = (optionA, optionB) =>
        (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase());

    protected constructor(props) {
        super(props);
        this.state = {
            ...this.state,
            updateDisabled: true,
            createDisabled: true,
        };
    }

    componentDidMount() {
        this.refreshForm();
    }

    componentDidUpdate(prevProps: Readonly<MonitoredEventFormProps<T>>) {
        // Force refresh of form fields
        if (
            this.props.event?.id !== prevProps.event?.id ||
            this.props.event?.lastUpdate !== prevProps.event?.lastUpdate
        ) {
            this.refreshForm();
        }
    }

    protected abstract toFormValues(event: T): V;
    protected abstract fromFormValues(values: V);

    protected isUpdateDisabled() {
        if (!this.formRef.current?.getFieldsValue() || !this.props.event) return true;
        const values = this.formRef.current.getFieldsValue();
        const event = this.props.event;
        const hasSameValues =
            values.title === event.title &&
            TimeUtils.isEquals(values.validity?.[0]?.toDate(), event.validity?.from) &&
            TimeUtils.isEquals(values.validity?.[1]?.toDate(), event.validity?.to);
        return this.isCreateDisabled() || hasSameValues;
    }

    protected isCreateDisabled() {
        if (!this.formRef.current?.getFieldsValue()) return true;
        const values: MonitoredEventFormValues<T> = this.formRef.current.getFieldsValue();

        return (
            Utils.isEmptyString(values.title) ||
            Utils.isNotExists(values.validity?.[0]) ||
            Utils.isNotExists(values.validity?.[1])
        );
    }

    protected refreshForm() {
        this.formRef.current?.resetFields();
        const event = this.toFormValues(this.props.event);
        this.formRef.current?.setFieldsValue(event);
        this.setState({ updateDisabled: this.isUpdateDisabled(), createDisabled: this.isCreateDisabled() });
    }

    protected isNewEvent = () => Utils.isNotExists(this.props.event?.id);

    protected onSubmit = () => {
        const event = this.fromFormValues(this.formRef.current.getFieldsValue());
        this.isNewEvent() ? this.props.onCreate(event) : this.props.onUpdate(event);
    };

    protected static FORM_TITLE_ITEM = (
        <Form.Item label="Title" name="title">
            <Input className="title" />
        </Form.Item>
    );

    protected static FORM_DESCRIPTION_ITEM = (rows: number, label: string = 'Description') => (
        <Form.Item label={label} name="description">
            <Input.TextArea rows={rows} className="description" />
        </Form.Item>
    );

    protected static FORM_VALIDITY_ITEM = (
        <Form.Item label="Validity" name="validity">
            <DatePicker.RangePicker
                separator={<SwapRightOutlined />}
                showTime={{ format: 'HH:mm' }}
                format={'YYYY-MM-DD HH:mm'}
            />
        </Form.Item>
    );

    protected FORM_SELECT_ITEM = (
        label: string,
        name: string,
        options: any[],
        open: boolean,
        setOpen: (open: boolean) => void,
    ) => (
        <Form.Item label={label} name={name}>
            <Select
                size={'small'}
                mode="multiple"
                options={options}
                filterSort={this.selectOptionsSort}
                open={open}
                onDropdownVisibleChange={setOpen}
                onChange={() => setOpen(false)}
            />
        </Form.Item>
    );
}
